'use client';
import React, { useEffect, useRef, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/components/language/LanguageProvider';

interface HeroImage {
  id: string;
  url: string;
  alt: string;
}

interface HeroSectionProps {
  featuredImages: HeroImage[];
}

export default function HeroSection({ featuredImages: _featuredImages }: HeroSectionProps) {
  const parallaxRef = useRef<HTMLDivElement>(null);
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  const heroVideoSrc = '/uploads/artist/needle-loop.mp4';
  const heroVideoPoster = '/uploads/artist/artist-at-work.png';
  const heroPlaybackRate = 0.5;
  const { dictionary } = useLanguage();
  const applyHeroPlaybackRate = (video: HTMLVideoElement) => {
    if (video.playbackRate !== heroPlaybackRate) {
      video.defaultPlaybackRate = heroPlaybackRate;
      video.playbackRate = heroPlaybackRate;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!parallaxRef.current) return;
      if (window.innerWidth < 768) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const scrolled = window.scrollY;
      parallaxRef.current.style.transform = `translateY(${scrolled * 0.3}px) scale(1.04)`;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative w-full min-h-screen overflow-hidden bg-[#0D0D0D]">
      <div ref={parallaxRef} className="absolute inset-0 will-change-transform">
        {!videoUnavailable ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster={heroVideoPoster}
            className="h-full w-full object-cover object-[center_30%] brightness-[0.95] contrast-[1.08] saturate-[1.02]"
            aria-label={dictionary.hero.backgroundAlt}
            onLoadedMetadata={(event) => {
              applyHeroPlaybackRate(event.currentTarget);
            }}
            onCanPlay={(event) => applyHeroPlaybackRate(event.currentTarget)}
            onPlay={(event) => applyHeroPlaybackRate(event.currentTarget)}
            onError={() => setVideoUnavailable(true)}
          >
            <source src={heroVideoSrc} type="video/mp4" />
          </video>
        ) : (
          <AppImage
            src={heroVideoPoster}
            alt={dictionary.hero.backgroundAlt}
            fill
            priority
            className="object-cover object-[center_30%] brightness-[0.9] contrast-[1.08] saturate-[1.02]"
            sizes="100vw"
          />
        )}
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/84 via-[#0D0D0D]/42 to-[#0D0D0D]/10 z-10" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0D0D0D]/70 via-[#0D0D0D]/22 to-transparent z-10" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/46 via-transparent to-transparent z-10" />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_62%_38%,rgba(255,255,255,0.18),rgba(255,255,255,0)_58%)] mix-blend-screen" />
      <div className="absolute inset-0 z-10 bg-[radial-gradient(circle_at_center,transparent_35%,rgba(0,0,0,0.24)_100%)]" />

      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/8 rounded-full blur-[120px] animate-blob z-10 pointer-events-none" />
      <div className="absolute bottom-1/3 left-1/3 w-72 h-72 bg-primary/5 rounded-full blur-[100px] animate-blob-delay z-10 pointer-events-none" />

      <div className="relative z-20 min-h-screen flex flex-col justify-end pb-16 sm:pb-20 md:pb-24 px-4 sm:px-6 md:px-12 lg:px-16 pointer-events-none">
        <div className="pointer-events-auto max-w-4xl rounded-3xl bg-gradient-to-r from-black/42 via-black/24 to-transparent backdrop-blur-[1px] px-4 py-5 sm:px-6 sm:py-6 md:px-8 md:py-7">
          <div className="flex items-center gap-3 mb-5 sm:mb-6 animate-enter delay-100">
            <div className="flex items-center gap-2 bg-primary/12 border border-primary/25 text-primary px-3.5 sm:px-4 py-1.5 rounded-full text-[9px] sm:text-[10px] font-bold tracking-[0.18em] sm:tracking-[0.2em] uppercase">
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L10 6H14L11 9.5L12 14L8 11.5L4 14L5 9.5L2 6H6L8 1Z" fill="#C8A96E" />
              </svg>
              {dictionary.hero.artist}
            </div>
          </div>

          <h1
            className="text-[clamp(2.4rem,12vw,6.5rem)] font-black text-white leading-[0.95] mb-5 animate-enter delay-200 tracking-tight"
            style={{ fontFamily: 'var(--font-fraunces)' }}
          >
            {dictionary.hero.titleLine1}
            <br />
            <em className="not-italic shimmer-gold">{dictionary.hero.titleLine2}</em>
          </h1>

          <p className="text-white/65 text-sm sm:text-base md:text-lg font-medium max-w-xl leading-[1.7] mb-8 sm:mb-10 animate-enter delay-300">
            {dictionary.hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 animate-enter delay-400 w-full max-w-md sm:max-w-none">
            <a
              href="#booking"
              className="group flex items-center justify-between gap-3 bg-primary text-primary-foreground pl-6 sm:pl-7 pr-3 py-3.5 rounded-full font-bold text-sm hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_rgba(200,169,110,0.4)] active:translate-y-0"
            >
              {dictionary.header.book}
              <span className="bg-black/20 p-2 rounded-full group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M7 7h10v10M7 17 17 7" />
                </svg>
              </span>
            </a>
            <a
              href="#portfolio"
              className="glass-light text-white/75 pl-6 sm:pl-7 pr-3 py-3.5 rounded-full font-semibold text-sm hover:bg-white/12 transition-all duration-300 flex items-center justify-between gap-3 hover:-translate-y-0.5"
            >
              {dictionary.header.nav.portfolio}
              <span className="bg-white/10 p-2 rounded-full">
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
